import type { MailItem, MailState } from "../meta/mail";
import type { AfterActionRecord } from "../meta/save";
import { THEATERS } from "../mission/variants";

// Entry screen, framed as the Bravo FOB Operations Center (docs/design/
// HUB.md): launch or continue an Operation, visit Personnel or the Armory,
// or drop to the Range. The bulletin board posts one after-action card per
// finished Operation. Pure renderer.

export type MainMenuState = {
  hasActiveRun: boolean;
  opsPoints: number;
  bestDepth: number;
  campaignDay: number;
  log: AfterActionRecord[];
  mail: MailState;
  canPullMail: boolean;
};

const BOARD_CARDS = 4;

function mailItem(item: MailItem): string {
  return `
    <div class="mail-item mail-item--${item.rarity}">
      <span class="board-card__day">D${item.day.toString().padStart(3, "0")}</span>
      <span class="mail-item__rarity">[${item.rarity.toUpperCase()}]</span> ${item.text}
    </div>`;
}

function afterActionCard(card: AfterActionRecord): string {
  const theater = THEATERS[card.theater].codename.replace("OPERATION ", "");
  const kia = card.fallen.length > 0 ? ` · KIA ${card.fallen.join(", ")}` : "";
  return `
    <div class="board-card board-card--${card.outcome}">
      <span class="board-card__day">D${card.day.toString().padStart(3, "0")}</span>
      ${theater} · <span class="board-card__outcome">${card.outcome.toUpperCase()}</span>
      · ${card.depth}/5 NODES · ${card.kills} KILLS${kia}
    </div>`;
}

export class MainMenuScreen {
  private readonly el: HTMLElement;
  private state: MainMenuState = {
    hasActiveRun: false, opsPoints: 0, bestDepth: 0, campaignDay: 0, log: [],
    mail: { lastPullDay: -1, log: [] }, canPullMail: false,
  };

  constructor(private readonly callbacks: {
    onContinueRun: () => void;
    onNewRun: () => void;
    onArmory: () => void;
    onBarracks: () => void;
    onTestRange: () => void;
    onMailPull: () => void;
  }) {
    this.el = document.createElement("div");
    this.el.id = "main-menu";
    this.el.className = "screen-overlay screen-overlay--stacked";
    this.el.style.zIndex = "400";
    document.body.appendChild(this.el);
    this.el.addEventListener("click", this.handleClick);
  }

  setState(state: MainMenuState): void {
    this.state = state;
    this.render();
  }

  private render(): void {
    const { hasActiveRun, opsPoints, bestDepth, campaignDay, log, mail, canPullMail } = this.state;
    const continueBtn = hasActiveRun
      ? `<button id="menu-continue" class="screen-button screen-button--large">CONTINUE OPERATION</button>`
      : "";
    const newLabel = hasActiveRun ? "NEW OPERATION (ABANDONS CURRENT)" : "NEW OPERATION";

    const mailButton = canPullMail
      ? `<button id="menu-mail" class="screen-button screen-button--small">SHAKE THE MAIL BAG</button>`
      : mail.lastPullDay >= 0 && campaignDay > 0
        ? `<span class="mail-empty">MAIL CALL AT NEXT CAMPAIGN DAY</span>`
        : "";
    const mailPile = mail.log.length > 0 || canPullMail
      ? `<div class="mail-bag">
           <div class="bulletin-board__title">MAIL CALL — CARE PACKAGE PILE (${mail.log.length})</div>
           ${mail.log.slice(0, 3).map(mailItem).join("")}
           ${mailButton ? `<div class="mail-actions">${mailButton}</div>` : ""}
         </div>`
      : "";

    const board = log.length > 0
      ? `<div class="bulletin-board">
           <div class="bulletin-board__title">BULLETIN BOARD — AFTER ACTION</div>
           ${log.slice(0, BOARD_CARDS).map(afterActionCard).join("")}
         </div>`
      : "";

    this.el.innerHTML = `
      <div class="screen-kicker">BRAVO FOB — OPERATIONS CENTER &nbsp;//&nbsp; 4TH SPECIAL OPERATIONS SQUADRON &nbsp;//&nbsp; TOP SECRET // NOFORN</div>
      <div class="screen-title">SPECTER 1-1</div>
      <div class="screen-copy" style="max-width:540px;text-align:center">
        Cold War, 1989. You are the gunner of a classified AC-130H. Alpha
        Team is on the ground and you are the only thing between them and
        annihilation. Theaters you ignore heat up. Survivors carry between
        missions. Losses are permanent. They go home or they don't.
      </div>
      <div class="menu-actions">
        ${continueBtn}
        <button id="menu-new" class="screen-button${hasActiveRun ? "" : " screen-button--large"}">${newLabel}</button>
        <button id="menu-armory" class="screen-button">ARMORY &nbsp;<span class="menu-ops">${opsPoints} OPS</span></button>
        <button id="menu-barracks" class="screen-button">PERSONNEL</button>
        <button id="menu-test" class="screen-button screen-button--muted">THE RANGE</button>
      </div>
      <div class="career-line">${
        campaignDay > 0 ? `CAMPAIGN DAY ${campaignDay.toString().padStart(3, "0")} &nbsp;·&nbsp; ` : ""
      }${bestDepth > 0 ? `BEST DEPTH: ${bestDepth}/5` : "NO OPERATIONS FLOWN YET"}</div>
      ${board}
      ${mailPile}
      <div class="screen-footer">CLICK CANVAS TO LOCK MOUSE &nbsp;|&nbsp; ESC = PAUSE &nbsp;|&nbsp; 1/2/3 = WEAPON &nbsp;|&nbsp; H = HOLD/GO &nbsp;|&nbsp; WASD = ORBIT &nbsp;|&nbsp; HOLD RMB = ZOOM</div>
    `;
  }

  show(): void {
    this.render();
    this.el.classList.add("is-visible");
  }

  hide(): void {
    this.el.classList.remove("is-visible");
  }

  private handleClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("#menu-mail")) this.callbacks.onMailPull();
    else if (target.closest("#menu-continue")) this.callbacks.onContinueRun();
    else if (target.closest("#menu-new")) this.callbacks.onNewRun();
    else if (target.closest("#menu-armory")) this.callbacks.onArmory();
    else if (target.closest("#menu-barracks")) this.callbacks.onBarracks();
    else if (target.closest("#menu-test")) this.callbacks.onTestRange();
  };
}
